import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { useAuth } from '@/context/auth/useAuth';

export const useRestaurantBranding = () => {
  const { user } = useAuth();
  const { 
    activeRestaurantDetails,
    restaurantDetails,
    fetchRestaurantDetails,
    selectedRestaurants
  } = useRestaurantContext();

  // Keep track of previously fetched restaurant IDs to prevent redundant fetches
  const previousIdsRef = useRef<string[]>([]);

  // Create a stable reference to restaurant IDs
  const restaurantIds = useMemo(() => {
    return user?.restaurants?.map(restaurant => restaurant.id) || [];
  }, [user?.restaurants]);

  // Only fetch if the restaurant IDs have changed
  useEffect(() => {
    if (restaurantIds.length > 0) {
      // Check if the IDs have changed
      const currentIds = JSON.stringify(restaurantIds.sort());
      const previousIds = JSON.stringify(previousIdsRef.current.sort());
      
      if (currentIds !== previousIds) {
        fetchRestaurantDetails(restaurantIds);
        previousIdsRef.current = [...restaurantIds];
      }
    }
  }, [fetchRestaurantDetails, restaurantIds]);

  // Memoize helper functions to maintain referential equality
  const getLogoUrl = useCallback(() => {
    if (activeRestaurantDetails?.logoUrl) {
      return activeRestaurantDetails.logoUrl;
    } else if (activeRestaurantDetails?.organisationLogoUrl) {
      return activeRestaurantDetails.organisationLogoUrl;
    }
    return null;
  }, [activeRestaurantDetails]);
  
  const getBrandColor = useCallback(() => {
    return activeRestaurantDetails?.primaryColor || '#0f172a'; // Default to a dark blue
  }, [activeRestaurantDetails]);

  const getSecondaryColor = useCallback(() => {
    return activeRestaurantDetails?.secondaryColor || '#a59456'; // Default to a gold/brown
  }, [activeRestaurantDetails]);

  const getRestaurantColors = useCallback(() => {
    return {
      primaryColor: activeRestaurantDetails?.primaryColor || '#c5b068',
      secondaryColor: activeRestaurantDetails?.secondaryColor || '#a59456'
    };
  }, [activeRestaurantDetails]);
  
  const getActiveName = useCallback(() => {
    if (activeRestaurantDetails) {
      return activeRestaurantDetails.name;
    } else if (user?.organisationName) {
      return user.organisationName;
    }
    return 'Restaurant Dashboard';
  }, [activeRestaurantDetails, user?.organisationName]);
  
  const isOrganisationView = useCallback(() => {
    return selectedRestaurants.includes('all') || selectedRestaurants.length === 0;
  }, [selectedRestaurants]);

  return {
    activeRestaurant: activeRestaurantDetails,
    allRestaurants: restaurantDetails,
    selectedRestaurantIds: selectedRestaurants,
    getLogoUrl,
    getBrandColor,
    getSecondaryColor,
    getRestaurantColors,
    getActiveName,
    isOrganisationView
  };
};
